import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";

import h from "../../../../helpers";
import { filterData } from "../../../../helpers/materialTableFunctions";
import { toJson } from "../../../../helpers/typedHelpers";
import { SaveUserFeatures } from "../../../../actions/userActions";
import { SaveCompanyFeatures } from "../../../../actions/companiesActions";
import { showModal } from "../../../../actions/actionCreators";
import { GetCompanyFeatures, setCompanyNewSelectedFeatures } from "../../../../actions/featuresActions";
import FeaturesCurrCompanyTable from "../../../tables/FeaturesCurrCompanyTable";
import { IAppState } from "../../../../types/stores";
import { IUserCompanyFeature, ISelectedFeature, IFeature } from "../../../../types/stores/feature";

type Props = {
    isOpen: boolean;
    handleLoading: Function;
    allowCopyFeatures?: boolean;
    handleHaveChanges?: Function;
    companyId?: number;
    description?: string;
    isModal: boolean;
};

const SharedManageFeatures = forwardRef(
    (
        { isOpen, handleLoading, allowCopyFeatures = true, handleHaveChanges, companyId, description, isModal }: Props,
        ref
    ) => {
        const dispatch = useDispatch();

        // state
        const [filter, setFilter] = useState("");
        const [combinedFeatures, setCombinedFeatures] = useState<Array<IUserCompanyFeature>>([]); // original fixed list in state for filter reference
        const [filteredFeatures, setFilteredFeatures] = useState<Array<IUserCompanyFeature>>([]); // not sure what filtered features does, or if we need two feature lists, cant we just filter on the original list - vu
        const [selectedFeatures, setSelectedFeatures] = useState<Array<IUserCompanyFeature>>([]); // holds a local copy of features to persist changes
        const [userFeatures, setUserFeatures] = useState<Array<ISelectedFeature>>([]); // used to hold user features
        const [copiedFeatures, setCopiedFeatures] = useState<Array<ISelectedFeature>>([]);
        const [loading, setLoading] = useState(false); //used when loading userFeatures

        // props
        const selectedCompany = useSelector((state: IAppState) => state.session.companyId);
        const features = useSelector((state: IAppState) => state.companyEdit.features || []);
        const userId = useSelector((state: IAppState) => state.session.userId || 0);
        const companies = useSelector((state: IAppState) => state.session.companies).filter(
            x => x.CompanyID != selectedCompany
        );
        const companyNewSelectedFeatures = useSelector((state: IAppState) => state.features.companyNewSelectedFeatures);

        // find the list of features we removed/added (things that changed)
        const prevUserSelected = userFeatures.filter(x => x.Selected).map(x => x.Name);
        const prevCompanySelected = copiedFeatures.filter(x => x.Selected).map(x => x.Name);
        const newUserSelected = selectedFeatures.filter(x => x.UserSelected).map(x => x.Name);
        const userDiff = prevUserSelected
            .filter(x => !newUserSelected.includes(x))
            .concat(newUserSelected.filter(x => !prevUserSelected.includes(x)));
        let companyDiff = prevCompanySelected
            .filter(x => !companyNewSelectedFeatures.includes(x))
            .concat(companyNewSelectedFeatures.filter(x => !prevCompanySelected.includes(x)));

        // Set Company New Selected Features
        useEffect(() => {
            const newSelection = selectedFeatures.filter(x => x.CompanySelected).map(x => x.Name);
            dispatch(setCompanyNewSelectedFeatures(newSelection));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [selectedFeatures]);

        // Set Copied Features
        useEffect(() => {
            if (copiedFeatures.length == 0 && features.length > 0) {
                setCopiedFeatures([...features]);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [features]);

        // get company features
        useEffect(() => {
            if (selectedCompany != null && selectedCompany > 0 && isOpen) {
                dispatch(GetCompanyFeatures(companyId));
            }
        }, [dispatch, selectedCompany, isOpen, companyId]);

        // get user features
        useEffect(() => {
            if (userId > 0 && isOpen && copiedFeatures?.length > 0) {
                internalHandleLoading(true);
                const getUserFeaturesUrl = "/Features/GetUserFeatures?" + h.serialize({ userId });
                fetch(getUserFeaturesUrl, { credentials: "same-origin" })
                    .then(h.checkStatus)
                    .then(toJson)
                    .then(data => {
                        const response = data as unknown as { userFeatures: Array<IFeature> };
                        const userFeatures = response.userFeatures || [];
                        const formatUserFeatures = copiedFeatures.map(f => ({
                            Description: f.Description,
                            IsExternal: f.IsExternal,
                            IsUserOnly: f.IsUserOnly,
                            Name: f.Name,
                            Selected: userFeatures.map(c => c.Name).includes(f.Name),
                        }));
                        setUserFeatures(formatUserFeatures);
                        internalHandleLoading(false);
                    })
                    .catch(error => {
                        h.error("Error loading User Features.", error);
                        internalHandleLoading(false);
                    });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [copiedFeatures, userId, isOpen]);

        // set table data
        useEffect(() => {
            if (isOpen) {
                if (copiedFeatures.length > 0 && (!isModal || userFeatures.length > 0)) {
                    const userCompanyFeatures: Array<IUserCompanyFeature> = copiedFeatures.map(f => ({
                        Description: f.Description,
                        IsExternal: f.IsExternal,
                        IsUserOnly: f.IsUserOnly,
                        Name: f.Name,
                        UserSelected: userFeatures?.find(c => c.Name == f.Name)?.Selected || false,
                        CompanySelected: f.Selected,
                    }));
                    setCombinedFeatures(userCompanyFeatures);
                    setFilteredFeatures(userCompanyFeatures);
                    setSelectedFeatures(userCompanyFeatures);
                } else {
                    setFilteredFeatures([]);
                    setSelectedFeatures([]);
                }
            }
        }, [copiedFeatures, userFeatures, isOpen, isModal]);

        //handle filter
        useEffect(() => {
            if (isOpen) {
                setFilteredFeatures(filterData(combinedFeatures, filter, null));
            }
        }, [combinedFeatures, filter, isOpen]);

        //handle HaveChanges
        useEffect(() => {
            if (handleHaveChanges) {
                handleHaveChanges(userDiff?.length > 0 || companyDiff?.length > 0);
            }
        }, [userDiff.length, companyDiff.length, handleHaveChanges]);

        useImperativeHandle(ref, () => ({
            handleSetFilter(newFilter: string = "") {
                setFilter(newFilter);
            },
            handleSaveUpdateFeatures() {
                saveUpdateFeatures();
            },
        }));

        const saveUpdateFeatures = async () => {
            // if something changed - save it
            if (userDiff.length > 0) {
                dispatch(SaveUserFeatures(newUserSelected));
            }
            if (companyDiff.length > 0) {
                dispatch(SaveCompanyFeatures());
            }
        };

        const copyFeatures = (selectedCompany, selectedOption) =>
            dispatch(SaveCompanyFeatures(selectedCompany, selectedOption, true));

        const internalHandleLoading = (isLoading: boolean) => {
            handleLoading(isLoading);
            setLoading(isLoading);
        };

        return (
            <>
                <div style={{ margin: "0px 5px 10px 5px" }}>
                    {description && (
                        <div className="row">
                            <div className="col-sm-6">
                                <p>{description}</p>
                            </div>
                            {allowCopyFeatures && companies.length > 0 ? (
                                <div className="col-sm-6">
                                    <Button
                                        className="pull-right"
                                        color="primary"
                                        size="large"
                                        disabled={loading || companyNewSelectedFeatures?.length == 0}
                                        onClick={() => dispatch(showModal("COPY_FEATURES", { continue: copyFeatures }))}
                                    >
                                        Copy features
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    )}
                    {copiedFeatures?.length > 0 && (
                        <TextField
                            value={filter}
                            placeholder={"Search for displayed content in the table."}
                            onChange={e => setFilter(e.target.value)}
                            fullWidth
                        />
                    )}
                </div>
                <FeaturesCurrCompanyTable
                    filteredFeatures={filteredFeatures}
                    selectedFeatures={selectedFeatures}
                    filter={filter}
                    setFilteredFeatures={setFilteredFeatures}
                    setSelectedFeatures={setSelectedFeatures}
                    usersLoading={loading}
                    isModal={isModal}
                />
            </>
        );
    }
);

export default SharedManageFeatures;
